import { isPublicDiscoveryRecord, readBooleanLike } from "./eventDiscovery";

export type EventAccessDecision = {
  allowed: boolean;
  restricted: boolean;
};

const TERMINAL_STATUSES = new Set([
  "archived",
  "deleted",
  "draft",
  "inactive",
  "pending",
  "rejected",
  "removed",
  "review",
  "unpublished",
]);

const RESTRICTED_VALUES = new Set([
  "hidden",
  "internal",
  "invite",
  "invite-only",
  "invitation",
  "invitation-only",
  "jwt",
  "member",
  "members",
  "members-only",
  "noindex",
  "nonindex",
  "password",
  "private",
  "role",
  "token",
  "token-only",
  "unlisted",
]);

const PUBLIC_VISIBILITY_VALUES = new Set([
  "everyone",
  "listed",
  "open",
  "public",
  "visible",
]);
const PUBLIC_ACCESS_VALUES = new Set(["none", "open", "public"]);

const CREDENTIAL_FIELDS = new Set([
  "accessToken",
  "access_token",
  "inviteCode",
  "inviteToken",
  "invite_code",
  "invite_token",
  "invitationToken",
  "invitation_token",
  "token",
]);

export function isPublicVenueDetail(value: unknown): boolean {
  const record = asRecord(value);
  if (!record) return false;
  return isPublicDiscoveryRecord(record);
}

export function getEventDetailAccess(
  value: unknown,
  hasAuthorizedToken: boolean,
): EventAccessDecision {
  const record = asRecord(value);
  if (!record) return { allowed: false, restricted: false };

  if (
    hasTrue(record, [
      "deleted",
      "isDeleted",
      "is_deleted",
      "removed",
      "isRemoved",
      "is_removed",
    ])
  ) {
    return { allowed: false, restricted: false };
  }
  if (
    hasFalse(record, [
      "enabled",
      "isEnabled",
      "is_enabled",
      "published",
      "isPublished",
      "is_published",
    ])
  ) {
    return { allowed: false, restricted: false };
  }
  if (
    hasTimestamp(record, ["deletedAt", "deleted_at", "removedAt", "removed_at"])
  ) {
    return { allowed: false, restricted: false };
  }

  const status = normalized(record, [
    "status",
    "state",
    "publishStatus",
    "publish_status",
  ]);
  if (TERMINAL_STATUSES.has(status))
    return { allowed: false, restricted: false };

  const visibility = normalized(record, [
    "visibility",
    "privacy",
    "access",
    "accessLevel",
    "access_level",
  ]);
  const accessMode = normalized(record, [
    "access",
    "privateAccessMode",
    "private_access_mode",
    "accessMode",
    "access_mode",
    "authMode",
    "auth_mode",
  ]);
  const restricted =
    RESTRICTED_VALUES.has(status) ||
    (Boolean(visibility) && !PUBLIC_VISIBILITY_VALUES.has(visibility)) ||
    (Boolean(accessMode) && !PUBLIC_ACCESS_VALUES.has(accessMode)) ||
    hasTrue(record, [
      "private",
      "isPrivate",
      "is_private",
      "hidden",
      "isHidden",
      "is_hidden",
      "unlisted",
      "isUnlisted",
      "is_unlisted",
      "requiresToken",
      "requires_token",
      "tokenProtected",
      "token_protected",
    ]) ||
    hasFalse(record, ["discoverable", "isDiscoverable", "is_discoverable"]);

  return {
    allowed: !restricted || hasAuthorizedToken,
    restricted,
  };
}

export function stripEntityCredentials<T extends object>(value: T): T {
  return stripCredentials(value) as T;
}

export function fingerprintAccessToken(
  token: string | null | undefined,
): string {
  if (!token) return "public";

  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < token.length; index += 1) {
    const code = token.charCodeAt(index);
    first = Math.imul(first ^ code, 0x01000193);
    second = Math.imul(second ^ code, 0x85ebca6b);
    second ^= second >>> 13;
  }

  return `token-${toHex(first)}${toHex(second)}`;
}

function normalized(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (value === undefined || value === null) continue;
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, "-")
      .replace(/-+/g, "-");
  }
  return "";
}

function hasTrue(record: Record<string, unknown>, keys: string[]): boolean {
  return keys.some((key) => readBooleanLike(record[key]) === true);
}

function hasFalse(record: Record<string, unknown>, keys: string[]): boolean {
  return keys.some((key) => readBooleanLike(record[key]) === false);
}

function hasTimestamp(
  record: Record<string, unknown>,
  keys: string[],
): boolean {
  return keys.some((key) => String(record[key] ?? "").trim().length > 0);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function stripCredentials(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripCredentials);
  const record = asRecord(value);
  if (!record) return value;

  return Object.fromEntries(
    Object.entries(record).flatMap(([field, fieldValue]) =>
      CREDENTIAL_FIELDS.has(field)
        ? []
        : [[field, stripCredentials(fieldValue)]],
    ),
  );
}

function toHex(value: number): string {
  return (value >>> 0).toString(16).padStart(8, "0");
}
